import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  ArrowRight,
  Calendar,
  Check,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  ExternalLink,
  Globe,
  Heart,
  LifeBuoy,
  Mail,
  Sparkles,
} from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Card,
  Button,
  StatusBadge,
  type StatusTone,
  NotificationCard,
} from '@sighthost/ui';
import { supabase } from '@/shared/lib/supabase';
import { formatZARFromCents } from '@/shared/utils/format';
import { daysUntil } from '@/shared/utils/sastDate';

/**
 * /app/services/orders/:id  —  Customer-side service-order detail
 *
 * Phase 10 Slice 10.2, T-10.2-14.
 *
 * Read-only customer view of a Phase 10 service order (Studio Starter, Care
 * Plan, Email Setup). Mirrors the admin /admin/services/:id detail but with
 * customer-friendly copy and no lifecycle action buttons (those are staff-only
 * via service-order-mark-* EFs).
 *
 * Key sections:
 *   - Status timeline — Paid → Consultation → In Progress → Delivered → Complete
 *     with timestamps and a clear current-step marker. Hosting/Care/Email
 *     Setup orders collapse intermediate steps that don't apply.
 *   - Preview link card — visible once delivered_at is set; links to the
 *     primary_fqdn if present.
 *   - Free-hosting countdown — Studio Starter only, when studio_free_hosting
 *     row exists and converted_at is null. Shows days-to-cliff + auto-convert
 *     reminder.
 *   - Cliff converted notice — when converted_at is set, confirms hosting
 *     transitioned to SightHost Basic at R129/mo.
 *   - Installment plan — if installment_schedule row exists, shows progress
 *     and next-due date.
 *   - Linked ticket cross-link — surfaces the Phase 8d ticket the saga opened
 *     so the customer can reply with brief details / questions.
 *
 * RLS-enforced: service_order has policy `service_order_select_own` which
 * restricts SELECT to rows where customer_id = auth.uid(). Same for
 * studio_free_hosting and installment_schedule (own-only via service_order
 * join). Customers can only read their own orders — this page never needs
 * to check ownership in code.
 *
 * Spec: _bmad-output/PHASE_10_SERVICES_IMPLEMENTATION_PLAN.md T-10.2-14
 */

interface OrderDetail {
  id: string;
  sku_code: string;
  status: string;
  primary_fqdn: string | null;
  total_paid_cents: number;
  brief_completed_at: string | null;
  delivered_at: string | null;
  complete_at: string | null;
  slo_credit_issued_at: string | null;
  created_at: string;
  // Joined
  sku_display_name: string;
  sku_price_cents: number;
  // Optional
  ticket_id: string | null;
  free_hosting: {
    started_at: string;
    cliff_at: string;
    reminder_t15_sent: boolean;
    reminder_t5_sent: boolean;
    converted_at: string | null;
    cancelled_at: string | null;
  } | null;
  installment: {
    plan_months: number;
    installments_paid: number;
    installments_total: number;
    installment_cents: number;
    next_due_date: string | null;
  } | null;
}

const STATUS_TONE: Record<string, StatusTone> = {
  pending_payment: 'pending',
  paid: 'paid',
  awaiting_consultation: 'pending',
  in_progress: 'info',
  delivered: 'info',
  complete: 'active',
  cancelled: 'failed',
  failed_terminal: 'failed',
};

const STATUS_LABEL: Record<string, string> = {
  pending_payment: 'Awaiting payment',
  paid: 'Paid — preparing',
  awaiting_consultation: 'Awaiting consultation',
  in_progress: 'In progress',
  delivered: 'Delivered',
  complete: 'Complete',
  cancelled: 'Cancelled',
  failed_terminal: 'Failed',
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

// `daysUntil` now lives in @/shared/utils/sastDate. This copy clamped at 0 and
// rounded UP from the current instant, so a cliff 34 hours away read as "2 days"
// here and "1 day" on the admin side of the same record.

export default function MyServiceOrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const { data: orderRow, error: oErr } = await supabase
          .from('service_order')
          .select(
            `
            id, sku_code, status, primary_fqdn, total_paid_cents,
            brief_completed_at, delivered_at, complete_at, slo_credit_issued_at,
            created_at,
            service_sku:sku_code ( display_name, price_cents )
          `,
          )
          .eq('id', id)
          .maybeSingle();

        if (oErr) throw oErr;
        if (!orderRow) {
          if (!cancelled) {
            setError('Order not found, or you do not have access to it.');
            setLoading(false);
          }
          return;
        }

        const skuJoin = (orderRow as unknown as {
          service_sku: { display_name: string; price_cents: number } | null;
        }).service_sku;

        // Linked ticket — same audit_log trick the admin page uses
        let ticketId: string | null = null;
        const { data: auditRows } = await supabase
          .from('audit_log')
          .select('after')
          .eq('entity_kind', 'service_order')
          .eq('entity_id', orderRow.id)
          .eq('event', 'service_order.payment_received')
          .order('occurred_at', { ascending: false })
          .limit(1);
        if (auditRows && auditRows.length > 0) {
          const after = (auditRows[0] as { after: { ticket_id?: string } | null }).after;
          if (after && typeof after.ticket_id === 'string') ticketId = after.ticket_id;
        }

        // Studio extras (free hosting + installment) — only relevant for Studio Starter
        let freeHosting: OrderDetail['free_hosting'] = null;
        let installment: OrderDetail['installment'] = null;
        if (orderRow.sku_code === 'studio-starter') {
          const [fh, inst] = await Promise.all([
            supabase
              .from('studio_free_hosting')
              .select('started_at, cliff_at, reminder_t15_sent, reminder_t5_sent, converted_at, cancelled_at')
              .eq('service_order_id', orderRow.id)
              .maybeSingle(),
            supabase
              .from('installment_schedule')
              .select('plan_months, installments_paid, installments_total, installment_cents, next_due_date')
              .eq('service_order_id', orderRow.id)
              .maybeSingle(),
          ]);
          if (fh.data) freeHosting = fh.data as OrderDetail['free_hosting'];
          if (inst.data) installment = inst.data as OrderDetail['installment'];
        }

        if (cancelled) return;
        setOrder({
          id: orderRow.id,
          sku_code: orderRow.sku_code,
          status: orderRow.status,
          primary_fqdn: orderRow.primary_fqdn,
          total_paid_cents: orderRow.total_paid_cents,
          brief_completed_at: orderRow.brief_completed_at,
          delivered_at: orderRow.delivered_at,
          complete_at: orderRow.complete_at,
          slo_credit_issued_at: orderRow.slo_credit_issued_at,
          created_at: orderRow.created_at,
          sku_display_name: skuJoin?.display_name ?? orderRow.sku_code,
          sku_price_cents: skuJoin?.price_cents ?? 0,
          ticket_id: ticketId,
          free_hosting: freeHosting,
          installment,
        });
        setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const baseBreadcrumbs = [
    { label: 'Home', href: '/app' },
    { label: 'Services', href: '/app/services' },
  ];

  if (loading) {
    return (
      <UniversalPage
        title="Order"
        breadcrumbs={[...baseBreadcrumbs, { label: id?.slice(0, 8) ?? 'Detail' }]}
      >
        <UniversalLoadingState variant="spinner" caption="Loading your order…" />
      </UniversalPage>
    );
  }

  if (error || !order) {
    return (
      <UniversalPage
        title="Order"
        breadcrumbs={[...baseBreadcrumbs, { label: 'Not found' }]}
      >
        <NotificationCard
          tone="danger"
          title="Could not load order"
          body={error ?? 'No record matched that ID.'}
        />
      </UniversalPage>
    );
  }

  const isStudio = order.sku_code === 'studio-starter';
  const isCare = order.sku_code === 'care-pro';
  const previewUrl = order.primary_fqdn ? `https://${order.primary_fqdn}` : null;

  return (
    <UniversalPage
      title={order.sku_display_name}
      description={`Order ${order.id.slice(0, 8)} — placed ${formatDate(order.created_at)}`}
      breadcrumbs={[...baseBreadcrumbs, { label: order.id.slice(0, 8) }]}
      meta={
        <StatusBadge tone={STATUS_TONE[order.status] ?? 'pending'}>
          {STATUS_LABEL[order.status] ?? order.status}
        </StatusBadge>
      }
    >
      {/* Status timeline */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Progress</h2>
        </header>
        <ol className="space-y-0 px-6 py-4">
          <TimelineRow
            label="Order paid"
            timestamp={order.created_at}
            done
            icon={Check}
          />
          {isStudio && (
            <>
              <TimelineRow
                label="Brief gathered"
                description={
                  order.brief_completed_at
                    ? 'We collected everything we need from your consultation call. The 48-hour build clock has started.'
                    : 'We will call you shortly to gather pages, copy direction, brand colours, and your domain pick.'
                }
                timestamp={order.brief_completed_at}
                done={!!order.brief_completed_at}
                icon={ClipboardCheck}
              />
              <TimelineRow
                label="First preview"
                description={
                  order.delivered_at
                    ? 'Your site is live — share feedback if you want changes (2 revision rounds included).'
                    : 'Within 48 working hours of the consultation we will share a live preview link.'
                }
                timestamp={order.delivered_at}
                done={!!order.delivered_at}
                icon={Sparkles}
              />
            </>
          )}
          <TimelineRow
            label={isStudio ? 'Project complete' : isCare ? 'Subscription active' : 'Setup complete'}
            timestamp={order.complete_at}
            done={!!order.complete_at}
            icon={CheckCircle2}
            last
          />
        </ol>
      </Card>

      {/* Preview link — Studio Starter, once delivered */}
      {isStudio && order.delivered_at && previewUrl && (
        <Card tone="hairline" interactive={false} className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                <span className="text-primary">01/</span> Your site is live
              </p>
              <h3 className="font-display mt-2 text-lg font-bold tracking-tight text-ink">
                {order.primary_fqdn}
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Open it in a new tab to review. Feedback for revisions can be
                replied here or via your support ticket — we apply changes and
                send the next preview.
              </p>
            </div>
            <Button asChild variant="primary" size="default">
              <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                <Globe className="h-4 w-4" /> View site
                <ArrowRight className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </Card>
      )}

      {/* SLO credit notice */}
      {order.slo_credit_issued_at && (
        <NotificationCard
          tone="info"
          title="We missed our 48-hour SLA — extra month of free hosting on us"
          body={`You will receive 1 extra month of free SightHost Basic hosting added to your free-hosting period. The cliff date below already reflects the bonus.`}
        />
      )}

      {/* Free hosting countdown — Studio Starter, while active */}
      {isStudio && order.free_hosting && !order.free_hosting.converted_at && !order.free_hosting.cancelled_at && (
        <FreeHostingCountdownCard cliffAt={order.free_hosting.cliff_at} startedAt={order.free_hosting.started_at} />
      )}

      {/* Free hosting converted */}
      {isStudio && order.free_hosting?.converted_at && (
        <NotificationCard
          tone="info"
          title="Free hosting period ended"
          body={`Your hosting auto-converted to SightHost Basic at R129/month on ${formatDate(order.free_hosting.converted_at)}. You can switch plans or cancel from the Services page at any time.`}
        />
      )}

      {/* Installment progress — Studio Starter on payment plan */}
      {isStudio && order.installment && (
        <Card interactive={false}>
          <header className="border-b border-border px-6 py-3">
            <h2 className="font-display text-sm font-semibold text-ink">Payment plan</h2>
          </header>
          <div className="grid grid-cols-1 gap-4 px-6 py-4 md:grid-cols-3">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Plan</p>
              <p className="font-display mt-1 text-xl font-bold text-ink">
                {order.installment.plan_months}-month
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Progress</p>
              <p className="font-display mt-1 text-xl font-bold tabular-nums text-ink">
                {order.installment.installments_paid} / {order.installment.installments_total}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">Next charge</p>
              <p className="mt-1 text-sm tabular-nums text-foreground">
                {formatZARFromCents(order.installment.installment_cents)} ·{' '}
                {order.installment.next_due_date
                  ? formatDate(order.installment.next_due_date)
                  : 'no further charges'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Order summary + ticket cross-link */}
      <section className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card interactive={false}>
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="font-display text-sm font-semibold text-ink">Order summary</h2>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </header>
          <dl className="divide-y divide-border">
            <SummaryRow label="Price" value={formatZARFromCents(order.sku_price_cents)} />
            <SummaryRow label="Paid so far" value={formatZARFromCents(order.total_paid_cents)} />
            <SummaryRow label="Order ID" value={<code className="text-xs">{order.id.slice(0, 8)}</code>} />
            {isStudio && order.primary_fqdn && (
              <SummaryRow label="Domain" value={order.primary_fqdn} />
            )}
          </dl>
        </Card>

        <Card interactive={false}>
          <header className="flex items-center justify-between border-b border-border px-6 py-3">
            <h2 className="font-display text-sm font-semibold text-ink">Need a hand?</h2>
            <LifeBuoy className="h-4 w-4 text-muted-foreground" />
          </header>
          <div className="space-y-3 px-6 py-4 text-sm">
            {order.ticket_id ? (
              <>
                <p className="text-muted-foreground">
                  Your support ticket for this order is the fastest way to send feedback,
                  ask questions, or request changes.
                </p>
                <Link
                  to={`/app/support/${order.ticket_id}`}
                  className="inline-flex items-center gap-2 text-primary-ink hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  Open ticket {order.ticket_id.slice(0, 8)}
                  <ExternalLink className="h-3 w-3" />
                </Link>
              </>
            ) : (
              <>
                <p className="text-muted-foreground">
                  No ticket linked yet — open a new support request if you have feedback
                  or questions about this order.
                </p>
                <Link
                  to="/app/support/new"
                  className="inline-flex items-center gap-2 text-primary-ink hover:underline"
                >
                  <Mail className="h-4 w-4" />
                  Open a support ticket
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </>
            )}
          </div>
        </Card>
      </section>
    </UniversalPage>
  );
}

function TimelineRow({
  label,
  description,
  timestamp,
  done,
  icon: Icon,
  last,
}: {
  label: string;
  description?: string;
  timestamp: string | null;
  done: boolean;
  icon: React.ComponentType<{ className?: string }>;
  last?: boolean;
}) {
  return (
    <li className="relative flex gap-4 pb-4 last:pb-0">
      {!last && (
        <span
          aria-hidden="true"
          className={`absolute left-[15px] top-8 h-full w-0.5 ${done ? 'bg-primary/30' : 'bg-muted'}`}
        />
      )}
      <span
        className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 ${
          done ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-card text-muted-foreground'
        }`}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <div className="min-w-0 flex-1 pb-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className={`text-sm font-medium ${done ? 'text-ink' : 'text-muted-foreground'}`}>
            {label}
          </p>
          <p className="text-xs tabular-nums text-muted-foreground">{formatDate(timestamp)}</p>
        </div>
        {description && (
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{description}</p>
        )}
      </div>
    </li>
  );
}

function FreeHostingCountdownCard({ cliffAt, startedAt }: { cliffAt: string; startedAt: string }) {
  // The clamp is a display choice, not a date rule: "-3 days of free hosting
  // remaining" is nonsense to a customer, so a passed cliff reads as 0. It used
  // to be baked into a private daysUntil, which is how it silently applied to
  // every other caller of that copy too.
  const days = Math.max(0, daysUntil(cliffAt) ?? 0);
  const totalDays = Math.max(
    1,
    Math.round((new Date(cliffAt).getTime() - new Date(startedAt).getTime()) / 86_400_000),
  );
  const elapsed = totalDays - days;
  const pct = Math.min(100, Math.max(0, (elapsed / totalDays) * 100));

  return (
    <Card tone="hairline" interactive={false} className="p-6">
      <div className="flex items-start gap-4">
        <Heart className="h-8 w-8 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <span className="text-primary">02/</span> Free hosting
          </p>
          <h3 className="font-display mt-2 text-lg font-bold tracking-tight text-ink">
            {days} day{days === 1 ? '' : 's'} of free hosting remaining
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Auto-converts to SightHost Basic at R129/month on{' '}
            <strong className="text-ink">{formatDate(cliffAt)}</strong>. We will
            send reminder emails 15 and 5 days before. You can opt-out or switch plans
            anytime from your services list.
          </p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${pct}%` }}
              aria-label={`${Math.round(pct)}% of free hosting period elapsed`}
            />
          </div>
          <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
            <span>Started {formatDate(startedAt)}</span>
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> Cliff {formatDate(cliffAt)}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-6 py-3">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm tabular-nums text-ink">{value}</dd>
    </div>
  );
}
